import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RotateCcw, ShieldCheck, Brain, Activity } from "lucide-react";
import { LinesAudit } from "@/components/LinesAudit";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENGINE_LABEL = {
  cypher_all: "CYPHER · all targets", cypher_top5: "CYPHER · top 5 / team", trends_goal: "Trends · Top 3 scorers",
  trends_first: "Trends · Top 3 first goal", trends_multi: "Trends · Top 3 multi-goal", official_approved: "Official Card · APPROVED",
  official_watch: "Official Card · WATCH", atg_value: "ATG · VALUE picks", archetype: "Archetype-tagged players",
};
const fmtDate = (d) => `${d.slice(4, 6)}/${d.slice(6, 8)}`;

const Panel = ({ title, icon: Icon, right, children, testId }) => (
  <section className="rounded-xl border border-slate-800 bg-slate-900/30 p-3" data-testid={testId}>
    <div className="mb-2 flex items-center justify-between gap-2">
      <p className="flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wide text-cyan-200">{Icon && <Icon className="h-4 w-4 text-cyan-400" />}{title}</p>
      {right}
    </div>
    {children}
  </section>
);

const Kpi = ({ label, value, sub, testId }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2" data-testid={testId}>
    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="font-display text-2xl font-bold text-white">{value}</p>
    {sub && <p className="font-mono text-[10px] text-slate-500">{sub}</p>}
  </div>
);

const EngineTable = ({ r }) => {
  const nights = r.nights.filter((n) => n.graded).slice(0, 10);
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full font-mono text-[11px]" data-testid="audit-engine-table">
        <thead className="bg-slate-900 text-[9px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-2 py-2 text-left">Engine</th><th className="px-2 py-2 text-right">Picks</th>
            <th className="px-2 py-2 text-right">Hits</th><th className="px-2 py-2 text-right">Hit %</th>
            {nights.map((n) => <th key={n.date} className="px-2 py-2 text-right">{fmtDate(n.date)}</th>)}
          </tr>
        </thead>
        <tbody>
          {Object.entries(ENGINE_LABEL).map(([k, l]) => (
            <tr key={k} className="border-t border-slate-800/60" data-testid={`audit-engine-${k}`}>
              <td className="whitespace-nowrap px-2 py-1.5 text-slate-200">{l}</td>
              <td className="px-2 py-1.5 text-right text-slate-400">{r.totals[k]?.picks ?? 0}</td>
              <td className="px-2 py-1.5 text-right text-slate-300">{r.totals[k]?.hits ?? 0}</td>
              <td className="px-2 py-1.5 text-right font-bold text-amber-300">{r.totals[k]?.rate ?? "—"}{r.totals[k]?.rate != null && "%"}</td>
              {nights.map((n) => <td key={n.date} className="px-2 py-1.5 text-right text-slate-400">{n.engines?.[k]?.rate ?? "—"}</td>)}
            </tr>
          ))}
          <tr className="border-t border-slate-700" data-testid="audit-engine-goalie">
            <td className="px-2 py-1.5 text-cyan-200">Goalie accuracy (projected = actual)</td>
            <td className="px-2 py-1.5 text-right text-slate-400">{r.goalie_accuracy.total}</td>
            <td className="px-2 py-1.5 text-right text-slate-300">{r.goalie_accuracy.correct}</td>
            <td className="px-2 py-1.5 text-right font-bold text-cyan-300">{r.goalie_accuracy.rate ?? "—"}{r.goalie_accuracy.rate != null && "%"}</td>
            {nights.map((n) => <td key={n.date} className="px-2 py-1.5 text-right text-slate-400">{n.goalie_accuracy?.rate ?? "—"}</td>)}
          </tr>
        </tbody>
      </table>
      <p className="px-2 py-1.5 font-mono text-[9px] text-slate-600">Hit = scored a goal (first-goal list: scored the game's first goal · multi list: 2+ goals). Dressed players only.</p>
    </div>
  );
};

const Calibration = ({ data, actualLabel = "Actual goal %", testId = "audit-calibration" }) => (
  data.length ? (
    <div className="h-56" data-testid={testId}>
      <ResponsiveContainer>
        <ComposedChart data={data.map((d) => ({ ...d, label: `${d.bin}-${d.bin + 10}%` }))}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 10 }} />
          <YAxis tick={{ fill: "#64748b", fontSize: 10 }} unit="%" />
          <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 11 }} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <Bar dataKey="actual" name={actualLabel} fill="#f59e0b" radius={[3, 3, 0, 0]} />
          <Line dataKey="predicted" name="Model predicted %" stroke="#22d3ee" strokeWidth={2} dot />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  ) : <p className="py-6 text-center font-mono text-[11px] text-slate-600" data-testid={`${testId}-empty`}>Calibration appears after the first graded night.</p>
);

const WeightBars = ({ cur, def, testId }) => (
  <div className="space-y-1" data-testid={testId}>
    {Object.keys(def).map((k) => {
      const d = (cur[k] ?? def[k]) - def[k];
      return (
        <div key={k} className="flex items-center gap-2 font-mono text-[10px]">
          <span className="w-28 text-slate-400">{k}</span>
          <div className="h-1.5 flex-1 overflow-hidden rounded bg-slate-800"><div className="h-full bg-cyan-500" style={{ width: `${Math.min(cur[k] ?? def[k], 100) * 2}%` }} /></div>
          <span className="w-12 text-right text-slate-200">{(cur[k] ?? def[k]).toFixed(1)}</span>
          <span className={`w-12 text-right ${d > 0.05 ? "text-emerald-400" : d < -0.05 ? "text-rose-400" : "text-slate-600"}`}>{d > 0 ? "+" : ""}{d.toFixed(1)}</span>
        </div>
      );
    })}
  </div>
);

export default function AuditTab() {
  const [r, setR] = React.useState(null);
  const [err, setErr] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => {
    setErr(false);
    axios.get(`${API}/audit/report`, { params: { nights: 30 } }).then((x) => setR(x.data)).catch(() => setErr(true));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const reset = async () => {
    if (!window.confirm("Reset CYPHER and Game Trends weights to defaults?")) return;
    setBusy(true);
    try { await axios.post(`${API}/audit/reset-weights`); toast.success("Weights reset to defaults"); load(); }
    catch { toast.error("Reset failed"); } finally { setBusy(false); }
  };

  if (err) return <p className="py-24 text-center font-mono text-xs text-rose-400" data-testid="audit-error">Audit unavailable <button onClick={load} className="ml-2 underline">Retry</button></p>;
  if (!r) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-cyan-500" /></div>;
  const latest = r.nights.find((n) => n.graded);
  const learning = r.graded_nights >= r.min_nights;

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Picks + game lines frozen 4 PM ET (11 AM weekends) · graded 4 AM ET · weights auto-tune ±{r.step_cap} pts / week after {r.min_nights} graded nights</p>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Graded nights" value={r.graded_nights} sub={`${r.nights.length} snapshots`} testId="audit-kpi-nights" />
        <Kpi label="Goalie accuracy" value={r.goalie_accuracy.rate != null ? `${r.goalie_accuracy.rate}%` : "—"} sub={`${r.goalie_accuracy.correct}/${r.goalie_accuracy.total} starters`} testId="audit-kpi-goalie" />
        <Kpi label="Official APPROVED hit" value={r.totals.official_approved?.rate != null ? `${r.totals.official_approved.rate}%` : "—"} sub={`${r.totals.official_approved?.picks || 0} picks`} testId="audit-kpi-approved" />
        <Kpi label="Learning" value={learning ? "ACTIVE" : "WARMING UP"} sub={learning ? "weights tuning nightly" : `${r.graded_nights}/${r.min_nights} nights needed`} testId="audit-kpi-learning" />
        <Kpi label="Game lines record" value={r.lines?.totals?.all ? `${r.lines.totals.all.w}-${r.lines.totals.all.l}-${r.lines.totals.all.p}` : "—"} sub={r.lines?.totals?.all ? `${r.lines.totals.all.units > 0 ? "+" : ""}${r.lines.totals.all.units}u · ROI ${r.lines.totals.all.roi}%` : "awaiting first graded night"} testId="audit-kpi-lines" />
        <Kpi label="Best bets" value={r.lines?.totals?.best_bet ? `${r.lines.totals.best_bet.w}-${r.lines.totals.best_bet.l}-${r.lines.totals.best_bet.p}` : "—"} sub={r.lines?.totals?.best_bet ? `${r.lines.totals.best_bet.units > 0 ? "+" : ""}${r.lines.totals.best_bet.units}u` : "one per game"} testId="audit-kpi-bestbets" />
      </div>
      <Panel title="Nightly hit rates by engine" icon={Activity} testId="audit-engines"><EngineTable r={r} /></Panel>
      <LinesAudit lines={r.lines} Panel={Panel} Calibration={(p) => <Calibration {...p} actualLabel="Actual win %" testId="lines-calibration" />} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Calibration · predicted vs actual" icon={ShieldCheck} testId="audit-calibration-panel"><Calibration data={r.calibration} /></Panel>
        <Panel title="Model weights" icon={Brain} testId="audit-weights"
          right={<button data-testid="audit-reset-weights" disabled={busy} onClick={reset} className="flex items-center gap-1 rounded border border-slate-700 px-2 py-1 font-mono text-[10px] text-slate-300 transition-colors hover:border-rose-500 hover:text-rose-300 disabled:opacity-50"><RotateCcw className="h-3 w-3" /> Reset to defaults</button>}>
          <p className="mb-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">CYPHER (points · change vs default)</p>
          <WeightBars cur={r.weights.cypher} def={r.weights.default_cypher} testId="audit-weights-cypher" />
          <p className="mb-1 mt-3 font-mono text-[9px] uppercase tracking-widest text-slate-500">Game Trends funnel</p>
          <WeightBars cur={r.weights.trends} def={r.weights.default_trends} testId="audit-weights-trends" />
        </Panel>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Weight changes · what & why" icon={Brain} testId="audit-weight-history">
          {r.weights_history.length ? (
            <div className="max-h-64 space-y-1.5 overflow-y-auto">
              {r.weights_history.map((h, i) => (
                <div key={i} className="rounded border border-slate-800 bg-slate-950/50 px-2 py-1.5 font-mono text-[10px]" data-testid={`audit-history-${i}`}>
                  <span className={`mr-1.5 rounded px-1 text-[8px] font-bold uppercase ${h.kind === "reset" ? "bg-rose-950 text-rose-300" : "bg-cyan-950 text-cyan-300"}`}>{h.kind}</span>
                  <span className="text-slate-500">{new Date(h.at).toLocaleString()}</span>
                  <p className="mt-0.5 text-slate-300">{h.reason}</p>
                </div>
              ))}
            </div>
          ) : <p className="py-4 text-center font-mono text-[11px] text-slate-600">No weight changes yet — still on defaults.</p>}
        </Panel>
        <Panel title={`Goalie misses${latest ? ` · ${fmtDate(latest.date)}` : ""}`} icon={ShieldCheck} testId="audit-goalie-misses">
          {latest?.goalie_accuracy?.misses?.length ? latest.goalie_accuracy.misses.map((m, i) => (
            <p key={i} className="font-mono text-[11px] text-slate-300">{m.team}: projected <span className="text-rose-300">{m.projected}</span> → started <span className="text-emerald-300">{m.actual}</span></p>
          )) : <p className="py-4 text-center font-mono text-[11px] text-slate-600">{latest ? "All projected starters were correct." : "No graded night yet."}</p>}
        </Panel>
      </div>
    </div>
  );
}
